// Account section — read-only user info + entry points for the
// account-management flows (2FA, device tokens, secrets).
//
// The actual modal components live at App level so they can be
// triggered from either the user menu OR these link rows; here we
// just call the open callbacks the parent threads in.

import { useState } from "preact/hooks";
import { Icon } from "../../icons";
import type { Me } from "../../lib/use-me";
import { useUpdateCheck } from "../../lib/use-update-check";

interface SettingsAccountProps {
  me: Me | null;
  notify: (msg: string) => void;
  onOpenTotp: () => void;
  onOpenDeviceTokens: () => void;
  onOpenSecrets: () => void;
  onOpenDownloads: () => void;
}

export function SettingsAccount({
  me, notify,
  onOpenTotp, onOpenDeviceTokens, onOpenSecrets, onOpenDownloads,
}: SettingsAccountProps) {
  const update = useUpdateCheck();
  const [installing, setInstalling] = useState(false);
  const onCheck = async () => {
    const result = await update.manualCheck();
    if (result.surface === "browser") {
      notify(result.latestVersion
        ? `Latest tag is v${result.latestVersion}. Browser updates happen automatically — hard-refresh if needed.`
        : "No tagged release yet.");
      return;
    }
    if (result.surface === "old_wrapper") {
      // Pre-v0.2.5 wrappers lack UpdatesBridge so we can't one-tap
      // install. Send them straight to the APK file — the WebView's
      // download handler (or stock browser, if it punts) takes it
      // from there, and they sideload manually once.
      if (!result.latestVersion || !result.downloadUrl) {
        notify("No tagged release yet.");
        return;
      }
      window.location.href = result.downloadUrl;
      notify(`Downloading VultureChat v${result.latestVersion}… install manually once it lands.`);
      return;
    }
    if (!result.installedVersion) {
      notify("Couldn't read installed version from the wrapper.");
      return;
    }
    if (!result.latestVersion) {
      notify("No tagged release published yet.");
      return;
    }
    if (result.available) {
      notify(`Update available: v${result.latestVersion}. Tap Install in the banner.`);
    } else {
      notify(`Up to date · v${result.installedVersion}`);
    }
  };
  const onInstall = () => {
    setInstalling(true);
    const r = update.install();
    if (r === "ok") {
      notify(`Downloading v${update.latestVersion}…`);
    } else if (r === "error:permission_needed") {
      notify("Grant 'Install unknown apps' in settings, then tap Install again.");
      setInstalling(false);
    } else if (r !== "no_bridge" && r !== "no_update") {
      notify(`Install failed: ${r.replace(/^error:/, "")}`);
      setInstalling(false);
    } else {
      setInstalling(false);
    }
  };
  return (
    <div className="settings-section">
      <h3 className="settings-section-title">Account</h3>
      <p className="settings-section-sub">
        Who you're signed in as. Email + 2FA come from your admin profile.
      </p>

      <div className="settings-account-card">
        <div className="avatar settings-account-avatar">{me?.initial ?? "?"}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="settings-account-name">
            {me?.username ?? "Unknown"}
            <span className="settings-account-role">{me?.role ?? "user"}</span>
          </div>
          {me?.email
            ? <div className="settings-account-email">{me.email}</div>
            : <div className="settings-account-email settings-account-email-empty">No email set</div>}
        </div>
      </div>

      <div className="settings-row-group">
        <SettingsLinkRow
          icon="shield"
          label="Two-factor auth"
          sub={me?.role === "admin" ? "Required for admins" : "Add an extra factor at sign-in"}
          onClick={onOpenTotp}
        />
        <SettingsLinkRow
          icon="device"
          label="Device tokens"
          sub="Long-lived tokens for scripts and the Android app"
          onClick={onOpenDeviceTokens}
        />
        <SettingsLinkRow
          icon="key"
          label="Secrets"
          sub="API keys and credentials available to your agent"
          onClick={onOpenSecrets}
        />
        <SettingsLinkRow
          icon="device"
          label="Downloads"
          sub="Get the app — Android, desktop, and installer builds"
          onClick={onOpenDownloads}
        />
        <SettingsLinkRow
          icon="download"
          label={update.available ? `Update available · v${update.latestVersion}` : "Check for updates"}
          sub={
            update.checking
              ? "Checking…"
              : update.available
                ? "Tap to install"
                : update.installedVersion
                  ? `Installed v${update.installedVersion}${update.latestVersion ? ` · latest v${update.latestVersion}` : ""}`
                  : update.surface === "browser"
                    ? "Browser auto-updates · tap to see latest tag"
                    : update.surface === "old_wrapper"
                      ? update.latestVersion
                        ? `Old app · tap to download v${update.latestVersion} manually`
                        : "Old app · tap to fetch latest"
                      : "Tap to check"
          }
          onClick={update.available && !installing ? onInstall : onCheck}
          rightLabel={installing ? "…" : undefined}
        />
      </div>
    </div>
  );
}

interface SettingsLinkRowProps {
  icon: Parameters<typeof Icon>[0]["name"];
  label: string;
  sub?: string;
  rightLabel?: string;
  onClick?: () => void;
}
function SettingsLinkRow({ icon, label, sub, rightLabel, onClick }: SettingsLinkRowProps) {
  return (
    <button className="settings-link-row" onClick={onClick} type="button">
      <span className="settings-link-icon"><Icon name={icon} size={14} /></span>
      <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
        <div className="settings-link-label">{label}</div>
        {sub && <div className="settings-link-sub">{sub}</div>}
      </div>
      {rightLabel && <span className="settings-link-right">{rightLabel}</span>}
      <Icon name="arrow-right" size={13} />
    </button>
  );
}
